import { useState } from 'react';
import {
  Lock, Bell, Palette, User, Sun, Moon, Type, ArrowLeft, Sparkles, Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useClientSettings, accentClasses, ThemeAccent, ThemeMode } from './settings';

const accents: { key: ThemeAccent; label: string }[] = [
  { key: 'indigo', label: 'Indigo' },
  { key: 'emerald', label: 'Emerald' },
  { key: 'violet', label: 'Violet' },
  { key: 'sky', label: 'Sky' },
  { key: 'rose', label: 'Rose' },
];

const modes: { key: ThemeMode; label: string; icon: any }[] = [
  { key: 'light', label: 'light', icon: Sun },
  { key: 'dark', label: 'dark', icon: Moon },
];

export default function SettingsPage() {
  const { settings, update, t, themeMode, setThemeMode } = useClientSettings();
  const accent = accentClasses[settings.accent];

  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaved, setPwdSaved] = useState(false);

  const handleSavePwd = (e: React.FormEvent) => {
    e.preventDefault();
    setPwdSaved(true);
    setPwd({ current: '', next: '', confirm: '' });
    setTimeout(() => setPwdSaved(false), 2000);
  };

  return (
    <div className="space-y-5 pt-2">
      <div className="flex items-center gap-3">
        <Link to="/client-pwa" className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-secondary">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('settings')}</h1>
        </div>
      </div>

      {/* Account */}
      <Section icon={User} title={t('account')}>
        <Row label="Name" value="James Halloran" />
        <Row label="Email" value="james@jamestiling.com.au" />
        <Row label="Company" value="James Tiling Pty Ltd" />
      </Section>

      {/* Appearance */}
      <Section icon={Palette} title={t('appearance')}>
        <div className="space-y-4 px-4 py-3">
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">{t('theme_color')}</p>
            <div className="flex gap-2">
              {accents.map(a => {
                const isActive = settings.accent === a.key;
                const cls = accentClasses[a.key];
                return (
                  <button
                    key={a.key}
                    onClick={() => update('accent', a.key)}
                    className={`flex h-10 w-10 items-center justify-center rounded-full ${cls.bg} ring-offset-2 ring-offset-background transition-all ${
                      isActive ? `ring-2 ${cls.ring}` : 'opacity-70 hover:opacity-100'
                    }`}
                    aria-label={a.label}
                  >
                    {isActive && <Check className="h-4 w-4 text-white" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">{t('theme_mode')}</p>
            <div className="grid grid-cols-2 gap-2">
              {modes.map(m => {
                const Icon = m.icon;
                const isActive = themeMode === m.key;
                return (
                  <button
                    key={m.key}
                    onClick={() => setThemeMode(m.key)}
                    className={`flex flex-col items-center gap-1 rounded-xl border py-3 text-xs font-medium transition-colors ${
                      isActive
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/30'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{t(m.label)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <ToggleRow
            icon={Type}
            label={t('large_text')}
            value={settings.largeText}
            onChange={v => update('largeText', v)}
          />
        </div>
      </Section>

      {/* Notifications */}
      <Section icon={Bell} title={t('notifications')}>
        <ToggleRow
          label={t('email_notifications')}
          value={settings.emailNotifications}
          onChange={v => update('emailNotifications', v)}
        />
        <Divider />
        <ToggleRow
          label={t('push_notifications')}
          value={settings.pushNotifications}
          onChange={v => update('pushNotifications', v)}
        />
      </Section>

      {/* Password */}
      <Section icon={Lock} title={t('change_password')}>
        <form onSubmit={handleSavePwd} className="space-y-3 px-4 py-4">
          <PwdInput value={pwd.current} onChange={v => setPwd({ ...pwd, current: v })} placeholder={t('current_password')} />
          <PwdInput value={pwd.next} onChange={v => setPwd({ ...pwd, next: v })} placeholder={t('new_password')} />
          <PwdInput value={pwd.confirm} onChange={v => setPwd({ ...pwd, confirm: v })} placeholder={t('confirm_password')} />
          <button
            type="submit"
            disabled={!pwd.current || !pwd.next || pwd.next !== pwd.confirm}
            className={`mt-1 w-full rounded-xl py-3 text-sm font-semibold transition-colors ${
              pwdSaved
                ? 'bg-emerald-600 text-white'
                : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground'
            }`}
          >
            {pwdSaved ? `✓ ${t('saved')}` : t('save_changes')}
          </button>
        </form>
      </Section>

      {/* About */}
      <div className="pt-2 pb-4 text-center">
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t('powered_by')}</span>
          <div className={`flex items-center gap-1 rounded-full bg-gradient-to-r ${accent.gradient} px-2.5 py-1 text-white`}>
            <Sparkles className="h-3 w-3" />
            <span className="font-semibold">RoniDoc</span>
          </div>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground/70">v1.0.0</p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </h2>
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border px-4 py-3 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-border" />;
}

function ToggleRow({
  icon: Icon,
  label,
  value,
  onChange,
}: {
  icon?: any;
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full transition-colors ${value ? 'bg-primary' : 'bg-muted'}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
            value ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

function PwdInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <input
      type="password"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none"
    />
  );
}
